import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Button,
  Alert,
  TextField,
  Stack,
  Skeleton,
  FormControlLabel,
  Switch,
  MenuItem,
} from "@mui/material";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { useMaterialReactTable } from "material-react-table";
import DataTable from "../../ui/data/DataTable";
import PageContainer from "../../ui/layout/PageContainer";
import ModalComponent from "../../components/ModalComponent/ModalComponent";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Cell,
} from "recharts";
import { getRequestWithToken } from "../../utils/ApiRequests";
import "./fbs.scss";

// ─── Helpers ───────────────────────────────────────────────────────────────

function CustomTabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`fbs-tabpanel-${index}`}
      aria-labelledby={`fbs-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return { id: `fbs-tab-${index}`, "aria-controls": `fbs-tabpanel-${index}` };
}

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const ACTIVITY_STATUS_STYLE = {
  none: { backgroundColor: "rgba(0,0,0,0.06)", color: "text.secondary" },
  fresh: { backgroundColor: "rgba(33,150,243,0.12)", color: "#0a5c99" },
  "not reached": { backgroundColor: "rgba(255,152,0,0.12)", color: "#bf6000" },
  "not contacted": { backgroundColor: "rgba(255,152,0,0.12)", color: "#bf6000" },
  "not answered": { backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c" },
  "in conversation": { backgroundColor: "rgba(156,39,176,0.12)", color: "#6a1b9a" },
  contacted: { backgroundColor: "rgba(33,150,243,0.12)", color: "#0a5c99" },
  interested: { backgroundColor: "rgba(76,175,80,0.12)", color: "#2e7d32" },
  "follow up": { backgroundColor: "rgba(255,152,0,0.12)", color: "#bf6000" },
  "call later": { backgroundColor: "rgba(255,152,0,0.12)", color: "#bf6000" },
  "not fit": { backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c" },
  "not interested": { backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c" },
  "wrong number": { backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c" },
  "do not call": { backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c" },
  "hired elsewhere": { backgroundColor: "rgba(76,175,80,0.12)", color: "#2e7d32" },
  invalid: { backgroundColor: "rgba(96,125,139,0.12)", color: "#37474f" },
  "invalid profile": { backgroundColor: "rgba(96,125,139,0.12)", color: "#37474f" },
  duplicate: { backgroundColor: "rgba(96,125,139,0.12)", color: "#37474f" },
};

function ActivityChip({ value }) {
  const style = ACTIVITY_STATUS_STYLE[value] || ACTIVITY_STATUS_STYLE.none;
  return <Chip label={value || "none"} size="small" sx={style} />;
}

function CustomYAxisTick({ x, y, payload }) {
  const label = payload?.value || "";
  const maxCharsPerLine = 20;
  const maxLines = 2;
  const lines = [];
  let currentLine = "";
  let truncated = false;
  const words = label.split(/[\s-]+/);

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const test = currentLine ? `${currentLine}-${word}` : word;
    if (test.length > maxCharsPerLine && currentLine) {
      if (lines.length + 1 >= maxLines) {
        lines.push(currentLine);
        truncated = true;
        break;
      }
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = test;
    }
  }
  if (!truncated && currentLine) {
    if (lines.length < maxLines) {
      lines.push(currentLine);
    } else {
      truncated = true;
    }
  }
  if (truncated && lines.length > 0) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] = last.length <= maxCharsPerLine - 1
      ? last + "\u2026"
      : last.slice(0, maxCharsPerLine - 1) + "\u2026";
  }

  const lineHeight = 14;
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text
          key={i}
          x={-6}
          y={(i - (lines.length - 1) / 2) * lineHeight}
          textAnchor="end"
          fill="#555"
          fontSize={11}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

const CHART_COLORS = ["#1976d2", "#42a5f5", "#66bb6a", "#ff9800", "#ef5350", "#ab47bc", "#26c6da", "#ffd54f", "#a5d6a7", "#ffcc02"];

// ─── Main Component ────────────────────────────────────────────────────────

const FbsDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(() => {
    const t = parseInt(searchParams.get("tab"), 10);
    return t >= 0 && t <= 4 ? t : 0;
  });

  const [isTesterFilter, setIsTesterFilter] = useState(false);

  // ── Tab 0: Summary ──
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [startDate, setStartDate] = useState("2026-01-01");
  const [endDate, setEndDate] = useState("");

  // ── Tab 0: Charts ──
  const [unlockTrendData, setUnlockTrendData] = useState([]);
  const [unlockTrendLoading, setUnlockTrendLoading] = useState(false);
  const [unlockTrendError, setUnlockTrendError] = useState("");
  const [unlockTrendFilter, setUnlockTrendFilter] = useState("weekly");

  const [roleDistData, setRoleDistData] = useState([]);
  const [roleDistLoading, setRoleDistLoading] = useState(false);
  const [roleDistError, setRoleDistError] = useState("");

  const [topEmployersData, setTopEmployersData] = useState([]);
  const [topEmployersLoading, setTopEmployersLoading] = useState(false);
  const [topEmployersError, setTopEmployersError] = useState("");

  // ── Tab 1: Employers ──
  const [employersData, setEmployersData] = useState([]);
  const [employersLoading, setEmployersLoading] = useState(false);
  const [employersError, setEmployersError] = useState("");
  const [employersPagination, setEmployersPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [employersPage, setEmployersPage] = useState(1);
  const [employersPageSize, setEmployersPageSize] = useState(20);
  const [employersSearchInput, setEmployersSearchInput] = useState("");
  const [employersSearch, setEmployersSearch] = useState("");
  const [employersSortBy, setEmployersSortBy] = useState("lastUnlockAt");
  const [employersOrder, setEmployersOrder] = useState("desc");
  const [employersStartDateInput, setEmployersStartDateInput] = useState("2026-01-01");
  const [employersEndDateInput, setEmployersEndDateInput] = useState("");
  const [employersStartDate, setEmployersStartDate] = useState("2026-01-01");
  const [employersEndDate, setEmployersEndDate] = useState("");
  const [employersFilterError, setEmployersFilterError] = useState("");

  // ── Employer Unlocks Modal ──
  const [unlocksModalOpen, setUnlocksModalOpen] = useState(false);
  const [unlocksModalData, setUnlocksModalData] = useState([]);
  const [unlocksModalLoading, setUnlocksModalLoading] = useState(false);
  const [unlocksModalError, setUnlocksModalError] = useState("");
  const [unlocksModalEmployerId, setUnlocksModalEmployerId] = useState(null);
  const [unlocksModalEmployerName, setUnlocksModalEmployerName] = useState("");
  const [unlocksModalPage, setUnlocksModalPage] = useState(1);
  const [unlocksModalPageSize, setUnlocksModalPageSize] = useState(20);
  const [unlocksModalPagination, setUnlocksModalPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });

  // ── Tab 2: Unlocked Candidates ──
  const [unlockedData, setUnlockedData] = useState([]);
  const [unlockedLoading, setUnlockedLoading] = useState(false);
  const [unlockedError, setUnlockedError] = useState("");
  const [unlockedPagination, setUnlockedPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [unlockedPage, setUnlockedPage] = useState(1);
  const [unlockedPageSize, setUnlockedPageSize] = useState(20);
  const [unlockedRoleInput, setUnlockedRoleInput] = useState("");
  const [unlockedSearchInput, setUnlockedSearchInput] = useState("");
  const [unlockedActivityInput, setUnlockedActivityInput] = useState("");
  const [unlockedStartDateInput, setUnlockedStartDateInput] = useState("2026-01-01");
  const [unlockedEndDateInput, setUnlockedEndDateInput] = useState("");
  const [unlockedRoleFilter, setUnlockedRoleFilter] = useState("");
  const [unlockedSearch, setUnlockedSearch] = useState("");
  const [unlockedActivityFilter, setUnlockedActivityFilter] = useState("");
  const [unlockedStartDate, setUnlockedStartDate] = useState("2026-01-01");
  const [unlockedEndDate, setUnlockedEndDate] = useState("");
  const [unlockedFilterError, setUnlockedFilterError] = useState("");

  // ── Tab 3: Role Analytics ──
  const [roleAnalyticsData, setRoleAnalyticsData] = useState([]);
  const [roleAnalyticsLoading, setRoleAnalyticsLoading] = useState(false);
  const [roleAnalyticsError, setRoleAnalyticsError] = useState("");
  const [roleAnalyticsPagination, setRoleAnalyticsPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [roleAnalyticsPage, setRoleAnalyticsPage] = useState(1);
  const [roleAnalyticsPageSize, setRoleAnalyticsPageSize] = useState(20);
  const [roleAnalyticsStartDateInput, setRoleAnalyticsStartDateInput] = useState("2026-01-01");
  const [roleAnalyticsEndDateInput, setRoleAnalyticsEndDateInput] = useState("");
  const [roleAnalyticsStartDate, setRoleAnalyticsStartDate] = useState("2026-01-01");
  const [roleAnalyticsEndDate, setRoleAnalyticsEndDate] = useState("");
  const [roleAnalyticsFilterError, setRoleAnalyticsFilterError] = useState("");

  // ── Tab 4: Activity Tracking ──
  const [activityData, setActivityData] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [activityPagination, setActivityPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize, setActivityPageSize] = useState(20);
  const [activityRoleInput, setActivityRoleInput] = useState("");
  const [activityStatusInput, setActivityStatusInput] = useState("");
  const [activityStartDateInput, setActivityStartDateInput] = useState("2026-01-01");
  const [activityEndDateInput, setActivityEndDateInput] = useState("");
  const [activityRoleFilter, setActivityRoleFilter] = useState("");
  const [activityStatusFilter, setActivityStatusFilter] = useState("");
  const [activityStartDate, setActivityStartDate] = useState("2026-01-01");
  const [activityEndDate, setActivityEndDate] = useState("");
  const [activityFilterError, setActivityFilterError] = useState("");

  // ─── Data Fetchers ──────────────────────────────────────────────────────

  const buildIsTesterParam = () => `&isTester=${isTesterFilter ? "true" : "false"}`;

  const fetchSummary = async (from, to) => {
    try {
      setSummaryLoading(true);
      setSummaryError("");
      const res = await getRequestWithToken(`/fbs-admin/summary?startDate=${from}&endDate=${to}${buildIsTesterParam()}`);
      setSummaryData(res?.data?.data || null);
    } catch (err) {
      setSummaryError(err?.response?.data?.msg || "Unable to load summary metrics.");
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchUnlockTrend = async (from, to, filter) => {
    try {
      setUnlockTrendLoading(true);
      setUnlockTrendError("");
      const query = new URLSearchParams();
      if (filter) query.set("filter", filter);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const res = await getRequestWithToken(`/fbs-admin/graphs/unlock-trend?${query.toString()}`);
      setUnlockTrendData(res?.data?.data || []);
    } catch (err) {
      setUnlockTrendError(err?.response?.data?.msg || "Unable to load unlock trend.");
    } finally {
      setUnlockTrendLoading(false);
    }
  };

  const fetchRoleDistribution = async (from, to) => {
    try {
      setRoleDistLoading(true);
      setRoleDistError("");
      const query = new URLSearchParams();
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const res = await getRequestWithToken(`/fbs-admin/graphs/role-distribution?${query.toString()}`);
      setRoleDistData(res?.data?.data || []);
    } catch (err) {
      setRoleDistError(err?.response?.data?.msg || "Unable to load role distribution.");
    } finally {
      setRoleDistLoading(false);
    }
  };

  const fetchTopEmployers = async (from, to) => {
    try {
      setTopEmployersLoading(true);
      setTopEmployersError("");
      const query = new URLSearchParams();
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const res = await getRequestWithToken(`/fbs-admin/graphs/top-employers?${query.toString()}`);
      setTopEmployersData(res?.data?.data || []);
    } catch (err) {
      setTopEmployersError(err?.response?.data?.msg || "Unable to load top employers.");
    } finally {
      setTopEmployersLoading(false);
    }
  };

  const fetchEmployers = async (params) => {
    try {
      setEmployersLoading(true);
      setEmployersError("");
      const { page, limit, search, sortBy, order, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (search) query.set("search", search);
      if (sortBy) query.set("sortBy", sortBy);
      if (order) query.set("order", order);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const res = await getRequestWithToken(`/fbs-admin/employers?${query.toString()}`);
      const resData = res?.data;
      setEmployersData(resData?.data || []);
      setEmployersPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (err) {
      setEmployersError(err?.response?.data?.msg || "Unable to load employers data.");
    } finally {
      setEmployersLoading(false);
    }
  };

  const fetchEmployerUnlocks = async (employerId, page = 1, limit = 20) => {
    try {
      setUnlocksModalLoading(true);
      setUnlocksModalError("");
      setUnlocksModalOpen(true);
      setUnlocksModalEmployerId(employerId);
      const res = await getRequestWithToken(`/fbs-admin/employers/${employerId}/unlocks?page=${page}&limit=${limit}`);
      setUnlocksModalData(res?.data?.data || []);
      setUnlocksModalPagination(res?.data?.pagination || { total: 0, page: 1, limit, pages: 1 });
    } catch (err) {
      setUnlocksModalError(err?.response?.data?.msg || "Unable to load unlock details.");
    } finally {
      setUnlocksModalLoading(false);
    }
  };

  const fetchUnlockedCandidates = async (params) => {
    try {
      setUnlockedLoading(true);
      setUnlockedError("");
      const { page, limit, role, search, activityStatus, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (role) query.set("role", role);
      if (search) query.set("search", search);
      if (activityStatus) query.set("activityStatus", activityStatus);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const res = await getRequestWithToken(`/fbs-admin/unlocked-candidates?${query.toString()}`);
      const resData = res?.data;
      setUnlockedData(resData?.data || []);
      setUnlockedPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (err) {
      setUnlockedError(err?.response?.data?.msg || "Unable to load unlocked candidates.");
    } finally {
      setUnlockedLoading(false);
    }
  };

  const fetchRoleAnalytics = async (params) => {
    try {
      setRoleAnalyticsLoading(true);
      setRoleAnalyticsError("");
      const { page, limit, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const res = await getRequestWithToken(`/fbs-admin/role-analytics?${query.toString()}`);
      const resData = res?.data;
      setRoleAnalyticsData(resData?.data || []);
      setRoleAnalyticsPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (err) {
      setRoleAnalyticsError(err?.response?.data?.msg || "Unable to load role analytics.");
    } finally {
      setRoleAnalyticsLoading(false);
    }
  };

  const fetchActivityTracking = async (params) => {
    try {
      setActivityLoading(true);
      setActivityError("");
      const { page, limit, role, activityStatus, from, to } = params;
      const query = new URLSearchParams();
      query.set("page", page);
      query.set("limit", limit);
      if (role) query.set("role", role);
      if (activityStatus) query.set("activityStatus", activityStatus);
      if (from) query.set("startDate", from);
      if (to) query.set("endDate", to);
      query.set("isTester", isTesterFilter ? "true" : "false");
      const res = await getRequestWithToken(`/fbs-admin/activity-tracking?${query.toString()}`);
      const resData = res?.data;
      setActivityData(resData?.data || []);
      setActivityPagination(resData?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (err) {
      setActivityError(err?.response?.data?.msg || "Unable to load activity tracking.");
    } finally {
      setActivityLoading(false);
    }
  };

  // ─── Effects ────────────────────────────────────────────────────────────

  useEffect(() => {
    const today = formatDate(new Date());
    setEndDate(today);
    setEmployersEndDate(today);
    setEmployersEndDateInput(today);
    setUnlockedEndDate(today);
    setUnlockedEndDateInput(today);
    setRoleAnalyticsEndDate(today);
    setRoleAnalyticsEndDateInput(today);
    setActivityEndDate(today);
    setActivityEndDateInput(today);
    fetchSummary(startDate, today);
    fetchUnlockTrend(startDate, today, "weekly");
    fetchRoleDistribution(startDate, today);
    fetchTopEmployers(startDate, today);
  }, []);

  // Re-fetch all when isTester toggles
  useEffect(() => {
    if (!endDate) return;
    fetchSummary(startDate, endDate);
    fetchUnlockTrend(startDate, endDate, unlockTrendFilter);
    fetchRoleDistribution(startDate, endDate);
    fetchTopEmployers(startDate, endDate);
  }, [isTesterFilter]);

  useEffect(() => {
    if (!employersEndDate) return;
    fetchEmployers({ page: employersPage, limit: employersPageSize, search: employersSearch, sortBy: employersSortBy, order: employersOrder, from: employersStartDate, to: employersEndDate });
  }, [employersPage, employersPageSize, employersSearch, employersSortBy, employersOrder, employersStartDate, employersEndDate, isTesterFilter]);

  useEffect(() => {
    if (!unlockedEndDate) return;
    fetchUnlockedCandidates({ page: unlockedPage, limit: unlockedPageSize, role: unlockedRoleFilter, search: unlockedSearch, activityStatus: unlockedActivityFilter, from: unlockedStartDate, to: unlockedEndDate });
  }, [unlockedPage, unlockedPageSize, unlockedRoleFilter, unlockedSearch, unlockedActivityFilter, unlockedStartDate, unlockedEndDate, isTesterFilter]);

  useEffect(() => {
    if (!roleAnalyticsEndDate) return;
    fetchRoleAnalytics({ page: roleAnalyticsPage, limit: roleAnalyticsPageSize, from: roleAnalyticsStartDate, to: roleAnalyticsEndDate });
  }, [roleAnalyticsPage, roleAnalyticsPageSize, roleAnalyticsStartDate, roleAnalyticsEndDate, isTesterFilter]);

  useEffect(() => {
    if (!activityEndDate) return;
    fetchActivityTracking({ page: activityPage, limit: activityPageSize, role: activityRoleFilter, activityStatus: activityStatusFilter, from: activityStartDate, to: activityEndDate });
  }, [activityPage, activityPageSize, activityRoleFilter, activityStatusFilter, activityStartDate, activityEndDate, isTesterFilter]);

  // ─── Filter Handlers ────────────────────────────────────────────────────

  const handleSummaryApply = () => {
    if (!startDate || !endDate) { setSummaryError("Please select both start date and end date."); return; }
    if (new Date(startDate) > new Date(endDate)) { setSummaryError("Start date cannot be after end date."); return; }
    setSummaryError("");
    fetchSummary(startDate, endDate);
    fetchUnlockTrend(startDate, endDate, unlockTrendFilter);
    fetchRoleDistribution(startDate, endDate);
    fetchTopEmployers(startDate, endDate);
  };

  const handleSummaryReset = () => {
    const today = formatDate(new Date());
    setStartDate("2026-01-01");
    setEndDate(today);
    setSummaryError("");
    setUnlockTrendFilter("weekly");
    fetchSummary("2026-01-01", today);
    fetchUnlockTrend("2026-01-01", today, "weekly");
    fetchRoleDistribution("2026-01-01", today);
    fetchTopEmployers("2026-01-01", today);
  };

  const handleEmployersApply = () => {
    if (!employersStartDateInput || !employersEndDateInput) { setEmployersFilterError("Please select both start date and end date."); return; }
    if (new Date(employersStartDateInput) > new Date(employersEndDateInput)) { setEmployersFilterError("Start date cannot be after end date."); return; }
    setEmployersFilterError("");
    setEmployersPage(1);
    setEmployersSearch(employersSearchInput);
    setEmployersStartDate(employersStartDateInput);
    setEmployersEndDate(employersEndDateInput);
  };

  const handleEmployersReset = () => {
    const today = formatDate(new Date());
    setEmployersSearchInput("");
    setEmployersSearch("");
    setEmployersSortBy("totalUnlocks");
    setEmployersOrder("desc");
    setEmployersStartDateInput("2026-01-01");
    setEmployersEndDateInput(today);
    setEmployersStartDate("2026-01-01");
    setEmployersEndDate(today);
    setEmployersPage(1);
    setEmployersFilterError("");
  };

  const handleUnlockedApply = () => {
    if (!unlockedStartDateInput || !unlockedEndDateInput) { setUnlockedFilterError("Please select both start date and end date."); return; }
    if (new Date(unlockedStartDateInput) > new Date(unlockedEndDateInput)) { setUnlockedFilterError("Start date cannot be after end date."); return; }
    setUnlockedFilterError("");
    setUnlockedPage(1);
    setUnlockedRoleFilter(unlockedRoleInput);
    setUnlockedSearch(unlockedSearchInput);
    setUnlockedActivityFilter(unlockedActivityInput);
    setUnlockedStartDate(unlockedStartDateInput);
    setUnlockedEndDate(unlockedEndDateInput);
  };

  const handleUnlockedReset = () => {
    const today = formatDate(new Date());
    setUnlockedRoleInput("");
    setUnlockedSearchInput("");
    setUnlockedActivityInput("");
    setUnlockedRoleFilter("");
    setUnlockedSearch("");
    setUnlockedActivityFilter("");
    setUnlockedStartDateInput("2026-01-01");
    setUnlockedEndDateInput(today);
    setUnlockedStartDate("2026-01-01");
    setUnlockedEndDate(today);
    setUnlockedPage(1);
    setUnlockedFilterError("");
  };

  const handleRoleAnalyticsApply = () => {
    if (!roleAnalyticsStartDateInput || !roleAnalyticsEndDateInput) { setRoleAnalyticsFilterError("Please select both start date and end date."); return; }
    if (new Date(roleAnalyticsStartDateInput) > new Date(roleAnalyticsEndDateInput)) { setRoleAnalyticsFilterError("Start date cannot be after end date."); return; }
    setRoleAnalyticsFilterError("");
    setRoleAnalyticsPage(1);
    setRoleAnalyticsStartDate(roleAnalyticsStartDateInput);
    setRoleAnalyticsEndDate(roleAnalyticsEndDateInput);
  };

  const handleRoleAnalyticsReset = () => {
    const today = formatDate(new Date());
    setRoleAnalyticsStartDateInput("2026-01-01");
    setRoleAnalyticsEndDateInput(today);
    setRoleAnalyticsStartDate("2026-01-01");
    setRoleAnalyticsEndDate(today);
    setRoleAnalyticsPage(1);
    setRoleAnalyticsFilterError("");
  };

  const handleActivityApply = () => {
    if (!activityStartDateInput || !activityEndDateInput) { setActivityFilterError("Please select both start date and end date."); return; }
    if (new Date(activityStartDateInput) > new Date(activityEndDateInput)) { setActivityFilterError("Start date cannot be after end date."); return; }
    setActivityFilterError("");
    setActivityPage(1);
    setActivityRoleFilter(activityRoleInput);
    setActivityStatusFilter(activityStatusInput);
    setActivityStartDate(activityStartDateInput);
    setActivityEndDate(activityEndDateInput);
  };

  const handleActivityReset = () => {
    const today = formatDate(new Date());
    setActivityRoleInput("");
    setActivityStatusInput("");
    setActivityRoleFilter("");
    setActivityStatusFilter("");
    setActivityStartDateInput("2026-01-01");
    setActivityEndDateInput(today);
    setActivityStartDate("2026-01-01");
    setActivityEndDate(today);
    setActivityPage(1);
    setActivityFilterError("");
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchParams({ tab: newValue }, { replace: true });
  };

  // ─── Summary Metrics ────────────────────────────────────────────────────

  const summaryMetrics = useMemo(() => {
    if (!summaryData) return [];
    return [
      {
        label: "Unlocks Overview",
        items: [
          { label: "Total Unlocks", value: summaryData.totalUnlocks ?? "-" },
          { label: "Active Unlocks", value: summaryData.activeUnlocks ?? "-" },
        ],
      },
      {
        label: "Employers & Roles",
        items: [
          { label: "Employers Using FBS", value: summaryData.totalEmployersUsingFBS ?? "-" },
          { label: "Roles Used", value: summaryData.totalRolesUsed ?? "-" },
        ],
      },
      {
        label: "PHC Economics",
        items: [
          { label: "Total PHC Spent", value: summaryData.totalPhcSpent !== undefined ? Number(summaryData.totalPhcSpent).toLocaleString() : "-" },
          { label: "Avg PHC / Unlock", value: summaryData.averagePhcPerUnlock ?? "-" },
        ],
      },
      {
        label: "Expiry Status",
        split: true,
        items: [
          { label: "Active Unlocks", value: summaryData.activeUnlocks ?? "-" },
          { label: "Expired Unlocks", value: summaryData.expiredUnlocks ?? "-" },
        ],
      },
    ];
  }, [summaryData]);

  // ─── Table Columns ──────────────────────────────────────────────────────

  const employersColumns = useMemo(() => [
    {
      accessorKey: "id", header: "S.No.", size: 80,
      Cell: ({ row }) => <span>{row.index + 1 + (employersPage - 1) * employersPageSize}</span>,
    },
    {
      accessorKey: "employerName", header: "Company", size: 200,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>{row.original.employerName || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{row.original.contactNumber || "-"}</Typography>
        </Box>
      ),
    },
    {
      accessorKey: "city", header: "Location", size: 130,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Typography variant="body2">{row.original.city || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{row.original.state || "-"}</Typography>
        </Box>
      ),
    },
    { accessorKey: "totalUnlocks", header: "Total Unlocks", size: 120 },
    {
      accessorKey: "totalPhcSpent", header: "PHC Spent", size: 110,
      Cell: ({ cell }) => Number(cell.getValue() || 0).toLocaleString(),
    },
    {
      accessorKey: "mostUnlockedRole", header: "Top Role", size: 160,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "normal" }}>{cell.getValue() || "-"}</Typography>,
    },
    {
      accessorKey: "lastUnlockAt", header: "Last Unlock", size: 150,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
      },
    },
    {
      accessorKey: "actions", header: "Unlocked Candidates", size: 180,
      enableSorting: false,
      Cell: ({ row }) => (
        <IconButton
          color="primary"
          title="View Unlocks"
          onClick={() => {
            setUnlocksModalEmployerName(row.original.employerName || "");
            setUnlocksModalPage(1);
            fetchEmployerUnlocks(row.original.employerId, 1, unlocksModalPageSize);
          }}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ], [employersPage, employersPageSize, unlocksModalPageSize]);

  const employersTable = useMaterialReactTable({
    columns: employersColumns, data: employersData,
    enableGlobalFilter: false, enableColumnFilters: false, enablePagination: false, enableSorting: false,
    layoutMode: "grid", state: { isLoading: employersLoading }, initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  const unlockedColumns = useMemo(() => [
    {
      accessorKey: "id", header: "S.No.", size: 80,
      Cell: ({ row }) => <span>{row.index + 1 + (unlockedPage - 1) * unlockedPageSize}</span>,
    },
    {
      accessorKey: "employerName", header: "Employer", size: 190,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>{row.original.employerName || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{row.original.employerPhone || "-"}</Typography>
        </Box>
      ),
    },
    {
      accessorKey: "candidateName", header: "Candidate", size: 180,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.original.candidateName || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{row.original.candidatePhone || "-"}</Typography>
        </Box>
      ),
    },
    {
      accessorKey: "role", header: "Role", size: 160,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "normal" }}>{cell.getValue() || "-"}</Typography>,
    },
    {
      accessorKey: "phcAmount", header: "PHC Cost", size: 100,
      Cell: ({ cell }) => cell.getValue() ?? "-",
    },
    {
      accessorKey: "unlockedAt", header: "Unlock Date", size: 140,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
      },
    },
    {
      accessorKey: "expiresAt", header: "Expiry", size: 140,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
      },
    },
    {
      accessorKey: "isActive", header: "Status", size: 100,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue() ? "Active" : "Expired"}
          size="small"
          sx={cell.getValue()
            ? { backgroundColor: "rgba(76,175,80,0.12)", color: "#2e7d32" }
            : { backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c" }}
        />
      ),
    },
    {
      accessorKey: "activityStatus", header: "Activity", size: 160,
      Cell: ({ cell }) => <ActivityChip value={cell.getValue()} />,
    },
  ], [unlockedPage, unlockedPageSize]);

  const unlockedTable = useMaterialReactTable({
    columns: unlockedColumns, data: unlockedData,
    enableGlobalFilter: false, enableColumnFilters: false, enablePagination: false, enableSorting: false,
    layoutMode: "grid", state: { isLoading: unlockedLoading }, initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  const roleAnalyticsColumns = useMemo(() => [
    {
      accessorKey: "id", header: "S.No.", size: 80,
      Cell: ({ row }) => <span>{row.index + 1 + (roleAnalyticsPage - 1) * roleAnalyticsPageSize}</span>,
    },
    {
      accessorKey: "roleName", header: "Role", size: 220,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>{cell.getValue() || "-"}</Typography>,
    },
    { accessorKey: "totalCandidatesAvailable", header: "Pool Size", size: 110 },
    { accessorKey: "totalUnlocks", header: "Total Unlocks", size: 120 },
    { accessorKey: "uniqueEmployers", header: "Employers", size: 110 },
    {
      accessorKey: "totalPhcSpent", header: "PHC Spent", size: 110,
      Cell: ({ cell }) => Number(cell.getValue() || 0).toLocaleString(),
    },
    {
      accessorKey: "unlockRate", header: "Unlock Rate", size: 120,
      Cell: ({ cell }) => `${cell.getValue() ?? 0}%`,
    },
  ], [roleAnalyticsPage, roleAnalyticsPageSize]);

  const roleAnalyticsTable = useMaterialReactTable({
    columns: roleAnalyticsColumns, data: roleAnalyticsData,
    enableGlobalFilter: false, enableColumnFilters: false, enablePagination: false, enableSorting: false,
    layoutMode: "grid", state: { isLoading: roleAnalyticsLoading }, initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  const activityColumns = useMemo(() => [
    {
      accessorKey: "id", header: "S.No.", size: 80,
      Cell: ({ row }) => <span>{row.index + 1 + (activityPage - 1) * activityPageSize}</span>,
    },
    {
      accessorKey: "employerName", header: "Employer", size: 190,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word", whiteSpace: "normal" }}>{row.original.employerName || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{row.original.employerPhone || "-"}</Typography>
        </Box>
      ),
    },
    {
      accessorKey: "candidateName", header: "Candidate", size: 160,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{cell.getValue() || "-"}</Typography>,
    },
    {
      accessorKey: "role", header: "Role", size: 160,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "normal" }}>{cell.getValue() || "-"}</Typography>,
    },
    {
      accessorKey: "activityStatus", header: "Activity Status", size: 180,
      Cell: ({ cell }) => <ActivityChip value={cell.getValue()} />,
    },
    {
      accessorKey: "unlockedAt", header: "Unlocked At", size: 150,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
      },
    },
    {
      accessorKey: "lastActivityAt", header: "Last Activity", size: 150,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
      },
    },
  ], [activityPage, activityPageSize]);

  const activityTable = useMaterialReactTable({
    columns: activityColumns, data: activityData,
    enableGlobalFilter: false, enableColumnFilters: false, enablePagination: false, enableSorting: false,
    layoutMode: "grid", state: { isLoading: activityLoading }, initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { borderBottom: "1px solid rgba(0,0,0,0.07)" } },
    muiTableContainerProps: { sx: { maxHeight: "520px" } },
  });

  // ─── Reusable Pagination ─────────────────────────────────────────────────

  const PaginationBar = ({ pagination, page, setPage, pageSize, setPageSize, loading }) => (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {pagination.total > 0
          ? `Showing page ${pagination.page} of ${pagination.pages} (${pagination.total} total)`
          : "No results"}
      </Typography>
      <TextField label="Rows" size="small" select value={pageSize}
        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} sx={{ width: 80 }}
      >
        {[10, 20, 50].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
      </TextField>
      <Button size="small" variant="outlined" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Prev</Button>
      <Button size="small" variant="outlined" disabled={page >= pagination.pages || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
    </Stack>
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <PageContainer
      title="FBS Dashboard"
      subtitle="Filter Based Search — Monitor candidate unlock activity, role analytics, and employer engagement"
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", pb: 1, position: "absolute", right: "50px" }}>
        <FormControlLabel
          control={<Switch checked={isTesterFilter} onChange={(e) => setIsTesterFilter(e.target.checked)} color="warning" />}
          label="Test Data"
        />
      </Box>

      <div className="fbs-dashboard">
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="FBS Dashboard Tabs" variant="scrollable" scrollButtons="auto">
            <Tab label="Dashboard / Metrics" {...a11yProps(0)} />
            <Tab label="Employers" {...a11yProps(1)} />
            <Tab label="Unlocked Candidates" {...a11yProps(2)} />
            <Tab label="Activity Tracking" {...a11yProps(3)} />
            {/* <Tab label="Role Analytics" {...a11yProps(4)} /> */}
          </Tabs>
        </Box>

        {/* ── TAB 0: Dashboard / Metrics ── */}
        <CustomTabPanel value={tabValue} index={0} >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} justifyContent={{ xs: "flex-start", sm: "flex-end" }} sx={{ mb: 2, p: 0 }}>
            <TextField label="Start Date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" onClick={handleSummaryApply} disabled={summaryLoading}>{summaryLoading ? "Applying..." : "Apply"}</Button>
            <Button variant="outlined" color="inherit" onClick={handleSummaryReset} disabled={summaryLoading}>Reset</Button>
          </Stack>

          {summaryError ? <Alert severity="warning" sx={{ mb: 2 }}>{summaryError}</Alert> : null}

          {/* KPI Cards */}
          <Grid container spacing={2}>
            {summaryLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                    <CardContent>
                      <Skeleton variant="text" width="50%" height={18} sx={{ mb: 1.5 }} />
                      <Grid container spacing={1}>
                        {[0, 1].map((j) => (
                          <Grid item xs={6} key={j}>
                            <Box sx={{ p: 1, borderRadius: 1, backgroundColor: "rgba(0,0,0,0.04)" }}>
                              <Skeleton variant="text" width="80%" height={14} />
                              <Skeleton variant="text" width="60%" height={32} sx={{ mt: 0.5 }} />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))
              : summaryMetrics.map((metric, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  {metric.split ? (
                    <Stack spacing={1} sx={{ height: "100%" }}>
                      {metric.items.map((item, j) => (
                        <Card key={j} className="fbs-metric-card" sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)", backgroundColor: "#fff", flex: 1 }}>
                          <CardContent sx={{ py: "12px !important" }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "primary.main", display: "block", mb: 0.75 }}>{item.label}</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{typeof item.value === "number" ? item.value.toLocaleString() : item.value}</Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Card className="fbs-metric-card" sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)", backgroundColor: "#fff" }}>
                      <CardContent>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "primary.main", display: "block", mb: 1.5 }}>{metric.label}</Typography>
                        <Grid container spacing={1}>
                          {metric.items.map((item, j) => (
                            <Grid item xs={6} key={j}>
                              <Box sx={{ p: 1, borderRadius: 1, backgroundColor: "rgba(25,118,210,0.04)", border: "1px solid rgba(25,118,210,0.08)", minHeight: 72, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>{item.label}</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2 }}>{typeof item.value === "number" ? item.value.toLocaleString() : item.value}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              ))}
          </Grid>

          {/* Charts Row 1 */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Unlock Trend</Typography>
                      <Typography variant="caption" color="text.secondary">PHC Spent (bars) &amp; Unlock Count (line)</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      {["today", "yesterday", "month", "weekly"].map((f) => (
                        <Button key={f} size="small"
                          variant={unlockTrendFilter === f ? "contained" : "outlined"}
                          disabled={unlockTrendLoading}
                          onClick={() => { setUnlockTrendFilter(f); fetchUnlockTrend(startDate, endDate, f); }}
                        >
                          {f === "weekly" ? "Weekly" : f.charAt(0).toUpperCase() + f.slice(1)}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                  {unlockTrendError ? <Alert severity="warning" sx={{ mb: 1 }}>{unlockTrendError}</Alert> : null}
                  {unlockTrendLoading
                    ? <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
                    : (
                      <ResponsiveContainer width="100%" height={240}>
                        <ComposedChart data={unlockTrendData} margin={{ top: 10, right: 40, left: 0, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="left" orientation="left" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                            label={{ value: "PHC", angle: -90, position: "insideLeft", offset: 10, fontSize: 11 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }}
                            label={{ value: "Unlocks", angle: 90, position: "insideRight", offset: 10, fontSize: 11 }} />
                          <RechartsTooltip />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar yAxisId="left" dataKey="phcSpent" name="PHC Spent" fill="#1976d2" radius={[3, 3, 0, 0]} />
                          <Line yAxisId="right" type="monotone" dataKey="unlockCount" name="Unlocks" stroke="#ff9800" strokeWidth={2} dot={{ r: 4, fill: "#ff9800" }} activeDot={{ r: 6 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Role Distribution</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Candidate unlocks by role</Typography>
                  {roleDistError ? <Alert severity="warning" sx={{ mb: 1 }}>{roleDistError}</Alert> : null}
                  {roleDistLoading
                    ? <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
                    : (
                      <ResponsiveContainer width="100%" height={Math.max(240, roleDistData.length * 44)}>
                        <BarChart data={roleDistData} layout="vertical" margin={{ top: 5, right: 60, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                          <YAxis dataKey="label" type="category" width={140} tick={<CustomYAxisTick />} interval={0} />
                          <RechartsTooltip />
                          <Bar dataKey="value" name="Unlocks" radius={[0, 3, 3, 0]}
                            label={{ position: "right", fontSize: 11, formatter: (v) => Number(v).toLocaleString() }}>
                            {roleDistData.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row 2 */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={12}>
              <Card sx={{ boxShadow: "var(--jy-card-shadow)", border: "1px solid var(--jy-border)" }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Top Employers by Unlocks</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Most active employers on FBS</Typography>
                  {topEmployersError ? <Alert severity="warning" sx={{ mb: 1 }}>{topEmployersError}</Alert> : null}
                  {topEmployersLoading
                    ? <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
                    : (
                      <ResponsiveContainer width="100%" height={Math.max(240, topEmployersData.length * 44)}>
                        <BarChart data={topEmployersData} layout="vertical" margin={{ top: 5, right: 60, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="label" type="category" width={140} tick={<CustomYAxisTick />} interval={0} />
                          <RechartsTooltip />
                          <Bar dataKey="value" name="Unlocks" fill="#66bb6a" radius={[0, 3, 3, 0]}
                            label={{ position: "right", fontSize: 11, formatter: (v) => Number(v).toLocaleString() }}>
                            {topEmployersData.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[(idx + 2) % CHART_COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CustomTabPanel>

        {/* ── TAB 1: Employers ── */}
        <CustomTabPanel value={tabValue} index={1}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" justifyContent={{ xs: "flex-start", sm: "flex-end" }} sx={{ mb: 2 }}>
            <TextField label="Search Company" size="small" value={employersSearchInput} onChange={(e) => setEmployersSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleEmployersApply(); }} sx={{ minWidth: 180 }} />
            <TextField label="Start Date" type="date" size="small" value={employersStartDateInput} onChange={(e) => setEmployersStartDateInput(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" size="small" value={employersEndDateInput} onChange={(e) => setEmployersEndDateInput(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Sort By" size="small" select value={employersSortBy}
              onChange={(e) => { setEmployersSortBy(e.target.value); setEmployersPage(1); }} sx={{ minWidth: 160 }}>
              <MenuItem value="totalUnlocks">Total Unlocks</MenuItem>
              <MenuItem value="totalPhcSpent">PHC Spent</MenuItem>
              <MenuItem value="lastUnlockAt">Last Unlock</MenuItem>
            </TextField>
            <TextField label="Order" size="small" select value={employersOrder}
              onChange={(e) => { setEmployersOrder(e.target.value); setEmployersPage(1); }} sx={{ minWidth: 140 }}>
              <MenuItem value="desc">Descending</MenuItem>
              <MenuItem value="asc">Ascending</MenuItem>
            </TextField>
            <Button variant="contained" onClick={handleEmployersApply} disabled={employersLoading}>{employersLoading ? "Loading..." : "Apply"}</Button>
            <Button variant="outlined" color="inherit" onClick={handleEmployersReset} disabled={employersLoading}>Reset</Button>
          </Stack>
          {employersFilterError ? <Alert severity="warning" sx={{ mb: 2 }}>{employersFilterError}</Alert> : null}
          {employersError ? <Alert severity="error" sx={{ mb: 2 }}>{employersError}</Alert> : null}
          <DataTable table={employersTable} />
          <PaginationBar pagination={employersPagination} page={employersPage} setPage={setEmployersPage} pageSize={employersPageSize} setPageSize={setEmployersPageSize} loading={employersLoading} />
        </CustomTabPanel>

        {/* ── TAB 2: Unlocked Candidates ── */}
        <CustomTabPanel value={tabValue} index={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" justifyContent={{ xs: "flex-start", sm: "flex-end" }} sx={{ mb: 2 }}>
            <TextField label="Role" size="small" placeholder="e.g. rider" value={unlockedRoleInput} onChange={(e) => setUnlockedRoleInput(e.target.value)} sx={{ minWidth: 150 }} />
            <TextField label="Search Employer / Candidate" size="small" value={unlockedSearchInput} onChange={(e) => setUnlockedSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUnlockedApply(); }} sx={{ minWidth: 200 }} />
            <TextField label="Activity Status" size="small" select value={unlockedActivityInput}
              onChange={(e) => setUnlockedActivityInput(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All</MenuItem>
              {["none", "not reached", "not contacted", "not answered", "contacted", "interested", "follow up", "call later", "not interested", "wrong number", "do not call", "hired elsewhere", "invalid profile", "duplicate"].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField label="Start Date" type="date" size="small" value={unlockedStartDateInput} onChange={(e) => setUnlockedStartDateInput(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" size="small" value={unlockedEndDateInput} onChange={(e) => setUnlockedEndDateInput(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" onClick={handleUnlockedApply} disabled={unlockedLoading}>{unlockedLoading ? "Loading..." : "Apply"}</Button>
            <Button variant="outlined" color="inherit" onClick={handleUnlockedReset} disabled={unlockedLoading}>Reset</Button>
          </Stack>
          {unlockedFilterError ? <Alert severity="warning" sx={{ mb: 2 }}>{unlockedFilterError}</Alert> : null}
          {unlockedError ? <Alert severity="error" sx={{ mb: 2 }}>{unlockedError}</Alert> : null}
          <DataTable table={unlockedTable} />
          <PaginationBar pagination={unlockedPagination} page={unlockedPage} setPage={setUnlockedPage} pageSize={unlockedPageSize} setPageSize={setUnlockedPageSize} loading={unlockedLoading} />
        </CustomTabPanel>

        {/* ── TAB 3: Role Analytics ── */}
        <CustomTabPanel value={tabValue} index={4}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" justifyContent={{ xs: "flex-start", sm: "flex-end" }} sx={{ mb: 2 }}>
            <TextField label="Start Date" type="date" size="small" value={roleAnalyticsStartDateInput} onChange={(e) => setRoleAnalyticsStartDateInput(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" size="small" value={roleAnalyticsEndDateInput} onChange={(e) => setRoleAnalyticsEndDateInput(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" onClick={handleRoleAnalyticsApply} disabled={roleAnalyticsLoading}>{roleAnalyticsLoading ? "Loading..." : "Apply"}</Button>
            <Button variant="outlined" color="inherit" onClick={handleRoleAnalyticsReset} disabled={roleAnalyticsLoading}>Reset</Button>
          </Stack>
          {roleAnalyticsFilterError ? <Alert severity="warning" sx={{ mb: 2 }}>{roleAnalyticsFilterError}</Alert> : null}
          {roleAnalyticsError ? <Alert severity="error" sx={{ mb: 2 }}>{roleAnalyticsError}</Alert> : null}
          <DataTable table={roleAnalyticsTable} />
          <PaginationBar pagination={roleAnalyticsPagination} page={roleAnalyticsPage} setPage={setRoleAnalyticsPage} pageSize={roleAnalyticsPageSize} setPageSize={setRoleAnalyticsPageSize} loading={roleAnalyticsLoading} />
        </CustomTabPanel>

        {/* ── TAB 4: Activity Tracking ── */}
        <CustomTabPanel value={tabValue} index={3}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" justifyContent={{ xs: "flex-start", sm: "flex-end" }} sx={{ mb: 2 }}>
            <TextField label="Role" size="small" placeholder="e.g. rider" value={activityRoleInput} onChange={(e) => setActivityRoleInput(e.target.value)} sx={{ minWidth: 150 }} />
            <TextField label="Activity Status" size="small" select value={activityStatusInput}
              onChange={(e) => setActivityStatusInput(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All</MenuItem>
              {["none", "not reached", "not contacted", "not answered", "contacted", "interested", "follow up", "call later", "not interested", "wrong number", "do not call", "hired elsewhere", "invalid profile", "duplicate"].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField label="Start Date" type="date" size="small" value={activityStartDateInput} onChange={(e) => setActivityStartDateInput(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" size="small" value={activityEndDateInput} onChange={(e) => setActivityEndDateInput(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" onClick={handleActivityApply} disabled={activityLoading}>{activityLoading ? "Loading..." : "Apply"}</Button>
            <Button variant="outlined" color="inherit" onClick={handleActivityReset} disabled={activityLoading}>Reset</Button>
          </Stack>
          {activityFilterError ? <Alert severity="warning" sx={{ mb: 2 }}>{activityFilterError}</Alert> : null}
          {activityError ? <Alert severity="error" sx={{ mb: 2 }}>{activityError}</Alert> : null}
          <DataTable table={activityTable} />
          <PaginationBar pagination={activityPagination} page={activityPage} setPage={setActivityPage} pageSize={activityPageSize} setPageSize={setActivityPageSize} loading={activityLoading} />
        </CustomTabPanel>
      </div>

      {/* ── Employer Unlocks Modal ── */}
      <ModalComponent
        openModal={unlocksModalOpen}
        setOpenModal={setUnlocksModalOpen}
        modalTitle={`Unlocked Candidates — ${unlocksModalEmployerName}`}
        handleOnCloseBtnClick={() => {
          setUnlocksModalOpen(false);
          setUnlocksModalData([]);
          setUnlocksModalError("");
          setUnlocksModalEmployerId(null);
          setUnlocksModalEmployerName("");
          setUnlocksModalPage(1);
          setUnlocksModalPagination({ total: 0, page: 1, limit: 20, pages: 1 });
        }}
        modalWidth="70%"
      >
        <Box sx={{ p: 2 }}>
          {unlocksModalError ? <Alert severity="error" sx={{ mb: 2 }}>{unlocksModalError}</Alert> : null}
          {unlocksModalLoading
            ? <Stack spacing={1}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />)}</Stack>
            : unlocksModalData.length === 0
              ? <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>No unlock records found for this employer.</Typography>
              : (
                <Box sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e0e0e0", textAlign: "left" }}>
                        <th style={{ padding: "8px 12px" }}>#</th>
                        <th style={{ padding: "8px 12px" }}>Candidate</th>
                        <th style={{ padding: "8px 12px" }}>Phone</th>
                        <th style={{ padding: "8px 12px" }}>Role</th>
                        <th style={{ padding: "8px 12px" }}>Region</th>
                        <th style={{ padding: "8px 12px" }}>Candidate City</th>
                        <th style={{ padding: "8px 12px" }}>PHC</th>
                        <th style={{ padding: "8px 12px" }}>Unlock Date</th>
                        <th style={{ padding: "8px 12px" }}>Expiry</th>
                        <th style={{ padding: "8px 12px" }}>Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unlocksModalData.map((c, i) => (
                        <tr key={c.candidateId || i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "8px 12px" }}>{i + 1 + (unlocksModalPage - 1) * unlocksModalPageSize}</td>
                          <td style={{ padding: "8px 12px", fontWeight: 500 }}>{c.candidateName || "-"}</td>
                          <td style={{ padding: "8px 12px" }}>{c.candidatePhone || "-"}</td>
                          <td style={{ padding: "8px 12px" }}>{c.role || "-"}</td>
                          <td style={{ padding: "8px 12px" }}>{c.regionName || "-"}</td>
                          <td style={{ padding: "8px 12px" }}>
                            {[c.candidateCity, c.candidateState].filter(Boolean).join(", ") || "-"}
                          </td>
                          <td style={{ padding: "8px 12px" }}>{c.phcAmount ?? "-"}</td>
                          <td style={{ padding: "8px 12px" }}>{c.unlockedAt ? new Date(c.unlockedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                          <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                            {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                            {c.isActive === false ? <Chip label="Expired" size="small" sx={{ ml: 0.5, backgroundColor: "rgba(244,67,54,0.12)", color: "#b71c1c", fontSize: 10 }} /> : null}
                          </td>
                          <td style={{ padding: "8px 12px" }}><ActivityChip value={c.activityStatus} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
          {unlocksModalPagination.total > 0 && (
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing page {unlocksModalPagination.page} of {unlocksModalPagination.pages} ({unlocksModalPagination.total} total)
              </Typography>
              <TextField label="Rows" size="small" select value={unlocksModalPageSize}
                onChange={(e) => { const s = Number(e.target.value); setUnlocksModalPageSize(s); setUnlocksModalPage(1); fetchEmployerUnlocks(unlocksModalEmployerId, 1, s); }}
                sx={{ width: 80 }}>
                {[10, 20, 50].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </TextField>
              <Button size="small" variant="outlined" disabled={unlocksModalPage <= 1 || unlocksModalLoading}
                onClick={() => { const p = unlocksModalPage - 1; setUnlocksModalPage(p); fetchEmployerUnlocks(unlocksModalEmployerId, p, unlocksModalPageSize); }}>Prev</Button>
              <Button size="small" variant="outlined" disabled={unlocksModalPage >= unlocksModalPagination.pages || unlocksModalLoading}
                onClick={() => { const p = unlocksModalPage + 1; setUnlocksModalPage(p); fetchEmployerUnlocks(unlocksModalEmployerId, p, unlocksModalPageSize); }}>Next</Button>
            </Stack>
          )}
        </Box>
      </ModalComponent>
    </PageContainer>
  );
};

export default FbsDashboard;
